import React, { useState, useEffect, useCallback } from "react";
import { Editor } from "@tiptap/react";
import {
  BsListNested,
  BsChevronRight,
  BsChevronDown,
} from "react-icons/bs";

interface HeadingItem {
  level: number;
  text: string;
  pos: number;
  id?: string;
}

interface DocumentOutlinePanelProps {
  editor: Editor;
  onClose?: () => void;
}

const DocumentOutlinePanel: React.FC<DocumentOutlinePanelProps> = ({
  editor,
  onClose,
}) => {
  const [headings, setHeadings] = useState<HeadingItem[]>([]);
  const [activePos, setActivePos] = useState<number | null>(null);
  const [collapsed, setCollapsed] = useState<Set<number>>(new Set());

  const extractHeadings = useCallback(() => {
    if (!editor) return;

    const items: HeadingItem[] = [];
    const { doc } = editor.state;

    doc.descendants((node, pos) => {
      if (node.type.name === "heading") {
        const level = node.attrs.level || 1;
        const text = node.textContent;
        const id = node.attrs.id;
        if (text.trim()) {
          items.push({ level, text, pos, id });
        }
      }
    });

    setHeadings(items);
  }, [editor]);

  // Extract headings on mount and on editor updates
  useEffect(() => {
    if (!editor) return;
    extractHeadings();

    const handler = () => extractHeadings();
    editor.on("update", handler);
    return () => {
      editor.off("update", handler);
    };
  }, [editor, extractHeadings]);

  const handleClick = useCallback(
    (pos: number) => {
      if (!editor) return;
      setActivePos(pos);

      // Set selection to the heading position
      editor.chain().focus().setTextSelection(pos + 1).run();

      // Scroll the heading into view
      const dom = editor.view.domAtPos(pos + 1);
      const element =
        dom.node instanceof HTMLElement
          ? dom.node
          : dom.node.parentElement;
      if (element) {
        const scrollContainer = element.closest(".tiptap-editor-content");
        if (scrollContainer) {
          const rect = element.getBoundingClientRect();
          const containerRect = scrollContainer.getBoundingClientRect();
          const targetTop =
            rect.top - containerRect.top + scrollContainer.scrollTop - 100;
          scrollContainer.scrollTo({
            top: Math.max(0, targetTop),
            behavior: "smooth",
          });
        }
      }
    },
    [editor]
  );

  const toggleCollapse = useCallback((pos: number) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(pos)) {
        next.delete(pos);
      } else {
        next.add(pos);
      }
      return next;
    });
  }, []);

  // Determine which headings are visible (not collapsed by parent)
  const getVisibleHeadings = useCallback(() => {
    const visible: HeadingItem[] = [];
    let skipBelow = Infinity;

    for (const heading of headings) {
      if (heading.level > skipBelow) {
        continue;
      }
      skipBelow = Infinity;
      visible.push(heading);

      if (collapsed.has(heading.pos)) {
        skipBelow = heading.level;
      }
    }
    return visible;
  }, [headings, collapsed]);

  // Check if a heading has children
  const hasChildren = useCallback(
    (index: number) => {
      if (index >= headings.length - 1) return false;
      return headings[index + 1].level > headings[index].level;
    },
    [headings]
  );

  const visibleHeadings = getVisibleHeadings();

  return (
    <div className="tiptap-outline-panel">
      <div className="tiptap-outline-header">
        <BsListNested />
        <span>Outline</span>
        {onClose && (
          <button
            className="tiptap-outline-close"
            onClick={onClose}
            title="Close outline"
          >
            &times;
          </button>
        )}
      </div>
      <div className="tiptap-outline-content">
        {visibleHeadings.length === 0 ? (
          <div className="tiptap-outline-empty">
            No headings found. Add headings to see the document outline.
          </div>
        ) : (
          <ul className="tiptap-outline-list">
            {visibleHeadings.map((heading) => {
              const originalIndex = headings.indexOf(heading);
              const isCollapsible = hasChildren(originalIndex);

              return (
                <li
                  key={heading.pos}
                  className={`tiptap-outline-item tiptap-outline-level-${heading.level} ${
                    activePos === heading.pos ? "active" : ""
                  }`}
                  style={{ paddingLeft: `${(heading.level - 1) * 16 + 8}px` }}
                >
                  {isCollapsible ? (
                    <button
                      className="tiptap-outline-toggle"
                      onClick={() => toggleCollapse(heading.pos)}
                    >
                      {collapsed.has(heading.pos) ? (
                        <BsChevronRight />
                      ) : (
                        <BsChevronDown />
                      )}
                    </button>
                  ) : (
                    <span className="tiptap-outline-toggle-spacer" />
                  )}
                  <button
                    className="tiptap-outline-link"
                    onClick={() => handleClick(heading.pos)}
                    title={heading.text}
                  >
                    <span className="tiptap-outline-level-badge">
                      H{heading.level}
                    </span>
                    <span className="tiptap-outline-text">
                      {heading.text}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
};

export default DocumentOutlinePanel;
